import { DateInput } from 'src/modules/shared/components/atoms/inputs/date-input';
import { InlineEditableInputWrapper } from '../inline-editable-input-wrapper';
import type { InlineEditableInputWrapperProps } from '../inline-editable-input-wrapper';
import type { ChangeEvent } from 'react';
import { getInlineInputRemPadding } from '../../../../atoms/buttons/button/button.styles';
import {
  formatLegacyAwareCustomFieldDateInputValue,
  parseLegacyAwareCustomFieldDateInputValue,
} from 'src/modules/shared/utils/custom-field-date-legacy.util';

interface InlineDateInputProps
  extends Omit<InlineEditableInputWrapperProps, 'children'> {
  placeholder?: string;
  min?: string;
  max?: string;
  type?: 'date' | 'datetime-local';
  disableCopy?: boolean;
}

export const InlineDateInput: React.FC<InlineDateInputProps> = ({
  placeholder,
  min,
  max,
  type = 'date',
  value: isoValue,
  onSave: parentOnSave,
  disableCopy = false,
  size = 'sm',
  ...props
}) => {
  const includeTime = type === 'datetime-local';

  const formatDateForInput = (isoDateValue: string): string =>
    formatLegacyAwareCustomFieldDateInputValue(isoDateValue, includeTime);

  const formatDisplayDate = (localizedDateValue: string): string => {
    if (!localizedDateValue) return '';

    try {
      if (type === 'datetime-local') {
        const date = new Date(localizedDateValue);
        if (isNaN(date.getTime())) return localizedDateValue;

        return (
          date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }) +
          ' ' +
          date.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        );
      }

      const [year, month, day] = localizedDateValue.split('-').map(Number);
      const localMidnight = new Date(year, month - 1, day);

      if (isNaN(localMidnight.getTime())) return localizedDateValue;

      return localMidnight.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return localizedDateValue;
    }
  };

  const handleSave = async (localizedDateValue: string) => {
    if (!localizedDateValue) {
      await parentOnSave('');
      return;
    }

    try {
      const parsed = parseLegacyAwareCustomFieldDateInputValue(
        localizedDateValue,
        includeTime
      );
      if (!parsed) {
        console.error('Invalid date value:', localizedDateValue);
        return;
      }

      await parentOnSave(parsed);
    } catch (error) {
      console.error('Error saving date:', error);
    }
  };

  const initialRaw = formatDateForInput(isoValue);
  const inlineInputPadding = getInlineInputRemPadding(size, 1);

  return (
    <InlineEditableInputWrapper
      {...props}
      value={initialRaw}
      onSave={handleSave}
      formatDisplayValue={formatDisplayDate}
      disableCopy={disableCopy}
      size={size}
    >
      {({ value: raw, onChange, disabled }) => (
        <DateInput
          className="w-full"
          style={{ paddingRight: `${inlineInputPadding}rem` }}
          value={raw}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.value)
          }
          placeholder={placeholder ?? props.label}
          size={size}
          variant={props.variant}
          type={type}
          min={min}
          max={max}
          // Inline edit mode intentionally moves focus into the active input.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          disabled={disabled}
        />
      )}
    </InlineEditableInputWrapper>
  );
};
